// modules/approvals/components/EntityPreviewCard.jsx
//
// Renders the "what am I actually approving" document served by
// GET /approval/entity-preview.
//
// The displayMeta panel (RequestDetailsCard) answers WHICH request this is; this
// one answers WHAT IS INSIDE IT — for a manual journal, every debit and credit
// line with the fund each one moves; for a delete request, the full ledger entry
// that would disappear.
//
// It knows nothing about entity types. The backend previewProvider returns
// summary rows, tables, field groups and alerts tagged with a formatting `type`,
// and this component paints them. A module that registers no previewProvider
// yields kind: 'NONE' and the card renders nothing at all.

import React, { useEffect, useState } from 'react';
import {
  Card, Table, Descriptions, Tag, Typography, Alert, Spin, Space, List, Divider,
} from 'antd';
import { ProfileOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchEntityPreview } from '../api';

const { Text, Title, Paragraph } = Typography;

const TONE_COLOR = {
  success: 'green', warning: 'gold', error: 'red', info: 'blue', default: 'default',
};

/* ── Value formatting ─────────────────────────────────────────────────────── */

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const renderValue = (value, type, tone) => {
  if (value === null || value === undefined || value === '') return <Text type="secondary">—</Text>;

  switch (type) {
    case 'currency':
      // A zero side of a double-entry line is noise; show it muted.
      return Number(value) === 0
        ? <Text type="secondary">—</Text>
        : <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{money(value)}</Text>;
    case 'number':
      return <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(value).toLocaleString('en-IN')}</Text>;
    case 'date':
      return <Text>{dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : String(value)}</Text>;
    case 'datetime':
      return <Text>{dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY, hh:mm A') : String(value)}</Text>;
    case 'boolean':
      return <Tag color={value ? (TONE_COLOR[tone] || 'blue') : 'default'}>{value ? 'Yes' : 'No'}</Tag>;
    case 'badge':
      return <Tag color={TONE_COLOR[tone] || 'default'}>{String(value)}</Tag>;
    case 'code':
      return <Text code>{String(value)}</Text>;
    default:
      return <Text>{String(value)}</Text>;
  }
};

/* ── Section renderers ────────────────────────────────────────────────────── */

const TableSection = ({ section }) => {
  const columns = (section.columns || []).map((c) => ({
    title: c.label,
    dataIndex: c.key,
    key: c.key,
    align: c.align,
    width: c.width,
    ellipsis: c.type === 'text' && !c.width,
    render: (v, row) => renderValue(v, c.type, row.__tone?.[c.key]),
  }));

  // Totals are declared per column so the footer lines up under the numbers it
  // sums, rather than being a free-floating "Total: x" line.
  const totalsByKey = {};
  (section.totals || []).forEach((t) => { totalsByKey[t.key] = t; });
  const hasTotals = (section.totals || []).length > 0;

  return (
    <Table
      size="small"
      bordered
      columns={columns}
      dataSource={section.rows || []}
      pagination={false}
      scroll={{ x: 'max-content' }}
      summary={hasTotals ? () => (
        <Table.Summary fixed>
          <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
            {(section.columns || []).map((c, i) => {
              const t = totalsByKey[c.key];
              return (
                <Table.Summary.Cell key={c.key} index={i} align={c.align}>
                  {t ? renderValue(t.value, t.type) : null}
                </Table.Summary.Cell>
              );
            })}
          </Table.Summary.Row>
        </Table.Summary>
      ) : undefined}
    />
  );
};

const FieldsSection = ({ section }) => (
  <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
    {(section.fields || []).map((f, i) => (
      <Descriptions.Item key={`${f.label}-${i}`} label={f.label}>
        {renderValue(f.value, f.type, f.tone)}
      </Descriptions.Item>
    ))}
  </Descriptions>
);

const Section = ({ section }) => {
  let body = null;
  if (section.type === 'table') body = <TableSection section={section} />;
  else if (section.type === 'fields') body = <FieldsSection section={section} />;
  else if (section.type === 'list') {
    body = (
      <List
        size="small"
        bordered
        dataSource={section.items || []}
        renderItem={(item) => <List.Item>{String(item)}</List.Item>}
      />
    );
  } else if (section.type === 'text') {
    body = <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{section.text || '—'}</Paragraph>;
  }
  if (!body) return null;

  return (
    <div style={{ marginTop: 20 }}>
      {section.title && (
        <Title level={5} style={{ marginBottom: 10 }}>{section.title}</Title>
      )}
      {body}
      {section.note && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          {section.note}
        </Text>
      )}
    </div>
  );
};

/* ── Card ─────────────────────────────────────────────────────────────────── */

const EntityPreviewCard = ({ instanceId, style }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!instanceId) { setPreview(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEntityPreview(instanceId)
      .then((data) => { if (!cancelled) setPreview(data && data.kind === 'PREVIEW' ? data : null); })
      // Never let a preview failure stand between an approver and the decision —
      // the rest of the page (and the Approve/Reject buttons) still work.
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || 'Could not load the record behind this approval.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [instanceId]);

  if (loading) {
    return (
      <Card bordered={false} style={{ borderRadius: 12, textAlign: 'center', padding: 24, ...style }}>
        <Spin />
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          Loading the record behind this approval…
        </Text>
      </Card>
    );
  }
  if (error) return <Alert type="warning" showIcon message={error} style={style} />;
  if (!preview) return null;   // entity type registered no preview — nothing to add

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', ...style }}
      headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
      bodyStyle={{ padding: 24 }}
      title={
        <Space size={8} wrap>
          <ProfileOutlined />
          <span>{preview.title || 'Request'}</span>
          {preview.status?.label && (
            <Tag color={TONE_COLOR[preview.status.tone] || 'default'}>{preview.status.label}</Tag>
          )}
        </Space>
      }
    >
      {preview.subtitle && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>{preview.subtitle}</Text>
      )}

      {(preview.alerts || []).map((a, i) => (
        <Alert
          key={i}
          type={a.type || 'info'}
          showIcon
          message={a.message}
          description={a.description}
          style={{ marginBottom: 12 }}
        />
      ))}

      {(preview.summary || []).length > 0 && (
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
          {preview.summary.map((s, i) => (
            <Descriptions.Item key={`${s.label}-${i}`} label={s.label}>
              {renderValue(s.value, s.type, s.tone)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {(preview.sections || []).length > 0 && <Divider style={{ margin: '20px 0 0' }} />}
      {(preview.sections || []).map((s) => <Section key={s.key} section={s} />)}
    </Card>
  );
};

export default EntityPreviewCard;
